// src/App.js
import React from 'react';
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import HeaderSection from './HeaderSection';
import OptionsSection from './OptionsSection';
import TestimonialsSection from './TestimonialsSection';
import PropertiesSectionSold from './PropertiesSectionSold';
import ContactFormSection from './ContactFormSection';
import FooterSection from './FooterSection';
import SalesGraph from './SalesGraph';
import { CssBaseline, Box } from '@mui/material';
import MessengerBubble from './MessengerBubble';

function App() {
  return (
    <div>
      <CssBaseline />

      <Helmet>
        <title>Ganesh Neupane | Top Michigan Realtor | American Dream Realty</title>
        <meta
          name="description"
          content="Work with Ganesh Neupane, a top-performing Michigan realtor fluent in English, Nepali, Hindi, and Urdu. Serving Troy, Novi, Auburn Hills & Rochester Hills with personalized, data-driven real estate services."
        />
        <meta
          name="keywords"
          content="Ganesh Neupane, Michigan realtor, Troy real estate, Novi real estate, Auburn Hills realtor, Rochester Hills homes, Nepali realtor, Hindi speaking realtor, Southeast Michigan homes, American Dream Realty, broker owner Michigan, multilingual realtor, first time home buyer Michigan, investment property Michigan"
        />

        {/* Person Schema - Ganesh Neupane */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Ganesh Neupane",
            "givenName": "Ganesh",
            "familyName": "Neupane",
            "jobTitle": "Broker/Owner",
            "description": "Top-performing Michigan real estate professional with expertise in residential sales, first-time home buyers, and investment properties. Fluent in English, Nepali, Hindi, and Urdu, serving diverse communities across Southeast Michigan.",
            "url": "https://ganeshneupane.com",
            "image": "https://ganeshneupane.com/me.JPG",
            "telephone": "+1-248-989-5737",
            "email": "americandreamrealty26@gmail.com",
            "knowsLanguage": [
              { "@type": "Language", "name": "English", "alternateName": "en" },
              { "@type": "Language", "name": "Nepali", "alternateName": "ne" },
              { "@type": "Language", "name": "Hindi", "alternateName": "hi" },
              { "@type": "Language", "name": "Urdu", "alternateName": "ur" }
            ],
            "worksFor": {
              "@type": "RealEstateAgent",
              "name": "American Dream Realty",
              "url": "https://americandreamrealty.homes"
            },
            "hasOccupation": {
              "@type": "Occupation",
              "name": "Real Estate Agent",
              "occupationLocation": {
                "@type": "State",
                "name": "Michigan"
              }
            },
            "areaServed": [
              { "@type": "City", "name": "Troy", "containedInPlace": { "@type": "State", "name": "Michigan" } },
              { "@type": "City", "name": "Novi", "containedInPlace": { "@type": "State", "name": "Michigan" } },
              { "@type": "City", "name": "Auburn Hills", "containedInPlace": { "@type": "State", "name": "Michigan" } },
              { "@type": "City", "name": "Rochester Hills", "containedInPlace": { "@type": "State", "name": "Michigan" } },
              { "@type": "City", "name": "Birmingham", "containedInPlace": { "@type": "State", "name": "Michigan" } },
              { "@type": "City", "name": "Bloomfield Hills", "containedInPlace": { "@type": "State", "name": "Michigan" } },
              { "@type": "City", "name": "Royal Oak", "containedInPlace": { "@type": "State", "name": "Michigan" } },
              { "@type": "City", "name": "Clarkston", "containedInPlace": { "@type": "State", "name": "Michigan" } }
            ],
            "sameAs": [
              "https://www.facebook.com/GaneshalpineRealtor/",
              "https://www.instagram.com/realtorganeshalpine",
              "https://www.zillow.com/profile/realtorganesh",
              "https://www.tiktok.com/@neupaneganeshrealtor",
              "https://americandreamrealty.homes"
            ]
          })}
        </script>

        {/* RealEstateAgent Schema - Business */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "American Dream Realty - Ganesh Neupane",
            "description": "Premier real estate services in Southeast Michigan. Ganesh Neupane, Broker/Owner, provides data-driven strategies for buyers, sellers, and investors. Multilingual support in English, Nepali, Hindi, and Urdu.",
            "url": "https://ganeshneupane.com",
            "telephone": "+1-248-989-5737",
            "email": "americandreamrealty26@gmail.com",
            "priceRange": "$$",
            "currenciesAccepted": "USD",
            "paymentAccepted": "Cash, Check, Wire Transfer",
            "areaServed": [
              { "@type": "City", "name": "Troy, MI" },
              { "@type": "City", "name": "Novi, MI" },
              { "@type": "City", "name": "Auburn Hills, MI" },
              { "@type": "City", "name": "Rochester Hills, MI" }
            ],
            "knowsLanguage": ["en", "ne", "hi", "ur"],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Real Estate Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Home Buying Assistance",
                    "description": "Full-service support for first-time and experienced home buyers including property search, market analysis, negotiations, and closing coordination."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Home Selling Services",
                    "description": "Comprehensive listing services including professional photography, marketing, pricing strategy, showings, and offer negotiations."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Investment Property Consulting",
                    "description": "Expert guidance for real estate investors on property valuations, rental potential, and portfolio building strategies."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Relocation Services",
                    "description": "Specialized support for families and professionals relocating to Southeast Michigan, with multilingual assistance available."
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Custom Home Construction",
                    "description": "End-to-end support for custom home building projects, from land acquisition and builder coordination to construction oversight and final walkthrough. We guide you through every phase of building your dream home."
                  }
                }
              ]
            },
            "sameAs": [
              "https://www.facebook.com/GaneshalpineRealtor/",
              "https://www.instagram.com/realtorganeshalpine",
              "https://www.zillow.com/profile/realtorganesh",
              "https://americandreamrealty.homes"
            ]
          })}
        </script>

        {/* FAQ Schema for AI Understanding */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What areas does Ganesh Neupane serve?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ganesh Neupane serves Southeast Michigan including Troy, Novi, Auburn Hills, Rochester Hills, Birmingham, Bloomfield Hills, Royal Oak, and Clarkston. He specializes in the Oakland County real estate market."
                }
              },
              {
                "@type": "Question",
                "name": "What languages does Ganesh Neupane speak?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ganesh Neupane is fluent in English, Nepali, Hindi, and Urdu. This multilingual capability allows him to serve diverse communities and immigrant families in Southeast Michigan."
                }
              },
              {
                "@type": "Question",
                "name": "Is Ganesh Neupane good for first-time home buyers?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Ganesh Neupane specializes in helping first-time home buyers. He provides patient guidance, explains every step of the process, and uses data-driven strategies to help clients find the right home within their budget."
                }
              },
              {
                "@type": "Question",
                "name": "What is American Dream Realty?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "American Dream Realty is a real estate brokerage based in Southeast Michigan. Ganesh Neupane is the Broker/Owner. The company focuses on data-driven strategies for buyers, sellers, and investors, with a mission to build wealth through real estate."
                }
              },
              {
                "@type": "Question",
                "name": "How can I contact Ganesh Neupane?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can reach Ganesh Neupane at 1-248-989-5737 or email americandreamrealty26@gmail.com. He is also available through Facebook Messenger, Instagram, and his websites ganeshneupane.com and americandreamrealty.homes."
                }
              }
            ]
          })}
        </script>

        {/* BreadcrumbList for Navigation */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://ganeshneupane.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Testimonials",
                "item": "https://ganeshneupane.com/#testimonials"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Properties",
                "item": "https://ganeshneupane.com/#properties"
              },
              {
                "@type": "ListItem",
                "position": 4,
                "name": "Contact",
                "item": "https://ganeshneupane.com/#contact"
              }
            ]
          })}
        </script>
      </Helmet>
      {/* Messenger Bubble */}
      <MessengerBubble />

      {/* Navbar */}
      <Navbar />

      {/* Home Section - Soft navy blue gradient */}
      <Box
        id="home"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          background: 'linear-gradient(135deg, #e8eef5, #d4e0ed)', // Soft navy-tinted white
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '1200px' }}>
          <HeaderSection />
        </Box>
      </Box>

      {/* Options Section - Warm white with subtle red undertone */}
      <Box
        id="options"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          background: 'linear-gradient(135deg, #faf8f8, #f5f0f0)', // Warm off-white with subtle warmth
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '1200px' }}>
          <OptionsSection />
        </Box>
      </Box>

      {/* Sales Graph Section - Subtle patriotic blue */}
      <Box
        id="salesgraph"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          background: 'linear-gradient(135deg, #edf1f7, #dce4f0)', // Soft blue with depth
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '1200px' }}>
          <SalesGraph />
        </Box>
      </Box>

      {/* Testimonials Section - Subtle red-white */}
      <Box
        id="testimonials"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          background: 'linear-gradient(135deg, #faf9f9, #f8f2f2)', // Very subtle warm rose-white
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '1200px' }}>
          <TestimonialsSection />
        </Box>
      </Box>

      {/* Properties Section - Deeper patriotic blue */}
      <Box
        id="properties"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          background: 'linear-gradient(135deg, #e5eaf3, #d0dae8)', // Richer navy undertone
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '1200px' }}>
          <PropertiesSectionSold />
        </Box>
      </Box>

      {/* Contact Section - Clean white with blue accent */}
      <Box
        id="contact"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          background: 'linear-gradient(135deg, #f8fafc, #eef3f8)', // Clean white to soft blue
        }}
      >
        <Box sx={{ width: '100%', maxWidth: '1200px' }}>
          <ContactFormSection />
        </Box>
      </Box>

      {/* Footer */}
      <FooterSection />

      {/* Messenger Bubble */}
      <MessengerBubble />
    </div>
  );
}

export default App;
